import React, { forwardRef, type KeyboardEvent } from 'react';
import Box from '../../atoms/Box';
import Text from '../../atoms/Text';
import MuiFaIcon from '../../atoms/MuiFaIcon';
import DatePicker, { registerLocale } from 'react-datepicker';
import type { Locale } from 'date-fns';
import { nb, sv, de, fr, it, fi, enGB } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';
import './calendarPicker.scss';

/**
 * Month and day names follow the signed-in user's language. enGB rather than
 * enUS so English users get D/M ordering like the rest of the product.
 */
const DATE_FNS_LOCALES: Record<string, Locale> = {
  nb,
  no: nb,
  sv,
  de,
  fr,
  it,
  fi,
  en: enGB,
};

Object.entries(DATE_FNS_LOCALES).forEach(([code, locale]) => {
  registerLocale(code, locale);
});

/** 'nb-NO' -> 'nb'. Falls back to English for anything unmapped. */
const localeCodeFor = (language?: string) => {
  const base = (language ?? 'en').split('-')[0].toLowerCase();
  return DATE_FNS_LOCALES[base] ? base : 'en';
};

const enterKey = 'Enter';
const spaceKey = ' ';

export type CalendarPickerProps = {
  date: Date;
  onChange?: (date: Date | null) => void;
  language?: string;
  withPortal?: boolean;
  datePickerProps?: Record<string, unknown>;
  displayOptions?: Intl.DateTimeFormatOptions;
  maxDate?: Date;
  minDate?: Date;
  dateFormat?: string;
  timeFormat?: string;
  /**
   * Portals the popup to document.body instead of rendering it inline. Pass a
   * stable id when the picker sits inside a dialog or any overflow-clipped
   * container, so the calendar is not cut off.
   */
  portalId?: string;

  // new optional functionality
  showTimeSelect?: boolean;
  showTimeSelectOnly?: boolean;
  /**
   * Stretch the trigger to fill its container. Without this the button is
   * content-sized, so two pickers showing different dates end up different
   * widths — visibly uneven in a from/until pair.
   */
  fullWidth?: boolean;
};

const CalendarPickerButton = forwardRef<
  HTMLDivElement,
  {
    value?: string;
    onClick?: () => void;
    isClickable: boolean;
  }
>(({ value, onClick, isClickable }, ref) => {
  const handleKeyDown = (event: KeyboardEvent) => {
    if (
      isClickable &&
      onClick &&
      (event.key === enterKey || event.key === spaceKey)
    ) {
      event.preventDefault();
      onClick();
    }
  };

  return (
    <div
      ref={ref}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      className={`calendar-picker-button${
        isClickable ? ' calendar-picker-button--clickable' : ''
      }`}
    >
      <MuiFaIcon
        name="fa-regular fa-calendar"
        className="calendar-picker-button__icon"
        sx={{ color: 'text.secondary' }}
      />
      <Text variant="body2" fontWeight={500}>
        {value}
      </Text>
    </div>
  );
});

CalendarPickerButton.displayName = 'CalendarPickerButton';

export default function CalendarPicker({
  date,
  onChange,
  language,
  withPortal = false,
  portalId,
  datePickerProps,
  displayOptions,
  maxDate,
  minDate,
  dateFormat = 'd MMM yyyy',
  timeFormat,
  showTimeSelect = false,
  showTimeSelectOnly = false,
  fullWidth = false,
}: CalendarPickerProps) {
  const formattedDate = showTimeSelectOnly
    ? date.toLocaleTimeString(language ?? 'en-GB', {
        hour: '2-digit',
        minute: '2-digit',
      })
    : date.toLocaleDateString(language ?? 'en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        ...(displayOptions ?? {}),
      });

  if (!onChange) {
    return <CalendarPickerButton isClickable={false} value={formattedDate} />;
  }

  const wrapperSx = fullWidth
    ? {
        display: 'block',
        width: '100%',
        // the global stylesheet makes these inline-block; override so the
        // trigger can actually fill the container
        '& .react-datepicker-wrapper': { display: 'block', width: '100%' },
        '& .react-datepicker__input-container': {
          display: 'block',
          width: '100%',
        },
        '& .calendar-picker-button': { width: '100%' },
      }
    : { display: 'inline-block' };

  return (
    <Box sx={wrapperSx}>
      <DatePicker
        selected={date}
        onChange={onChange}
        locale={localeCodeFor(language)}
        // Monday-first everywhere, matching the analytics day-of-week axis and
        // every European locale the product ships. The locale still drives the
        // month and weekday names.
        calendarStartDay={1}
        dateFormat={dateFormat}
        popperPlacement="bottom-end"
        withPortal={withPortal}
        portalId={portalId}
        maxDate={maxDate}
        minDate={minDate}
        timeFormat={timeFormat}
        showTimeSelect={showTimeSelect}
        showTimeSelectOnly={showTimeSelectOnly}
        {...(datePickerProps as any)}
        customInput={<CalendarPickerButton isClickable value={formattedDate} />}
      />
    </Box>
  );
}
